import {Component, Input, OnInit} from '@angular/core';
import * as lodash from 'lodash';
import {head, round, size} from 'lodash';
import {EmiSchedule} from '../../model/emi-schedule.model';
import {LendersInvested} from '../../model/loan.model';
import {EmiStatus} from '../../model/emi-status';
import {SplitMoney} from '../../service/lender-details.service';
import {PrepaymentStatus} from '../../model/prepayment-status';
import {EmiMoratoriumService} from '../../service/emi-moratorium.service';
import {EmiMoratorium} from '../../model/emi-moratorium.model';
import {WriteOff, WriteOffHistory, WriteOffTag} from '../../model/WriteOff';
import {WriteOffService} from '../../service/write-off.service';
import {LoanStatusType} from '../../model/loan-status.model';
import {ConfigService} from '../../service/config.service';
import {LenderReturnCalculator} from '../../service/lender-return-calculator';
import moment = require('moment');


@Component({
    selector: 'app-write-off',
    templateUrl: './write-off.component.html',
    styleUrls: ['./write-off.component.css'],
    providers: [EmiMoratoriumService]
})

export class WriteOffComponent implements OnInit {
    private static readonly SUB_STANDARD_ASSET_OVER_DUE_EMI_COUNT = 13;
    private static readonly LOSS_ASSET_OVER_DUE_EMI_COUNT = 25;

    private readonly emiMoratoriumService: EmiMoratoriumService;
    private readonly writeOffService: WriteOffService;
    private readonly configService: ConfigService;
    private readonly lenderReturnCalculator: LenderReturnCalculator;
    @Input()
    investedAmount: number;
    @Input()
    emiSchedule: EmiSchedule;
    @Input()
    lenderShare: number;
    @Input()
    prepaymentDetails: any;
    @Input()
    lendersInvested: LendersInvested[];
    @Input()
    loanId: string;
    @Input()
    loanSanctionStatus: LoanStatusType;

    emiSplit: SplitMoney;
    investmentDate: Date;
    maturityDate: Date;
    emiAmount: number;
    paidEmiCount: number;
    delayedEmiCount: number;
    subStandardDate: Date;
    expectedLossAssetDate: Date;
    principalOutstanding: number;
    subStandardAssetPrincipalWrittenOff: number;
    lossAssetPrincipalWrittenOff: number;
    prepaymentSplit: SplitMoney;
    currentNetLoss: number;
    expectedLoss: number;
    netInterestEarned: number;
    percentageOfSubStandardAssetPrincipalWrittenOff: number;
    percentageOfLossAssetPrincipalWrittenOff: number;
    capitalisedInterest: number;
    LoanStatusType = LoanStatusType;

    constructor(emiMoratoriumService: EmiMoratoriumService, writeOffService: WriteOffService, configService: ConfigService,
                lenderReturnCalculator: LenderReturnCalculator) {
        this.emiMoratoriumService = emiMoratoriumService;
        this.writeOffService = writeOffService;
        this.configService = configService;
        this.lenderReturnCalculator = lenderReturnCalculator;
    }


    async ngOnInit(): Promise<void> {
        await this.populateWrittenOffDetails();
    }

    private async populateWrittenOffDetails() {
        this.emiSplit = {} as SplitMoney;
        this.percentageOfSubStandardAssetPrincipalWrittenOff = this.configService.getSubStandardAssetWriteOffPercentage();
        this.percentageOfLossAssetPrincipalWrittenOff = this.configService.getLossAssetWriteOffPercentage();
        const emiMoratorium = await this.getEmiMoratorium(this.loanId);
        const writeOff = await this.writeOffService.getWriteOffByLoanId(this.loanId);
        this.capitalisedInterest = emiMoratorium ? emiMoratorium.compoundedAmountAtEndOfMoratorium.interest * this.lenderShare : 0;
        this.emiSplit.principal = this.getPaidEmiPrincipal(this.emiSchedule, this.lenderShare);
        this.emiSplit.interest = this.lenderReturnCalculator.getNetEmiInterestEarnedForLender(this.emiSchedule, this.lenderShare);
        this.prepaymentSplit = this.getPrepaymentSplit(this.prepaymentDetails, this.lenderShare);
        this.emiAmount = round(this.emiSchedule[size(this.emiSchedule) - 1].emi_amount * this.lenderShare, 2);
        this.paidEmiCount = size(this.emiSchedule.filter(emi => emi.status === EmiStatus.Completed || emi.status === EmiStatus.Received));
        this.delayedEmiCount = this.getDelayedEmiCount(this.emiSchedule);
        this.maturityDate = this.emiSchedule[size(this.emiSchedule) - 1].due_on;
        this.investmentDate = head(this.lendersInvested).timestamp;
        const subStandardAsset = this.getWriteOffBy(WriteOffTag.SubStandardAsset, writeOff);
        const lossAsset = this.getWriteOffBy(WriteOffTag.LossAsset, writeOff);
        this.subStandardDate = subStandardAsset ? subStandardAsset.createdAt : null;
        this.expectedLossAssetDate = this.getLossAssetDate(lossAsset);
        this.principalOutstanding = this.getPrincipalOutstanding();
        this.subStandardAssetPrincipalWrittenOff = subStandardAsset ? subStandardAsset.principal * this.lenderShare : 0;
        this.lossAssetPrincipalWrittenOff = lossAsset ? lossAsset.principal * this.lenderShare :
            this.getPrincipalOutstanding() - (subStandardAsset.principal * this.lenderShare);
        this.netInterestEarned = this.emiSplit.interest + this.prepaymentSplit.interest;
        this.currentNetLoss = this.subStandardAssetPrincipalWrittenOff - this.netInterestEarned;
        this.expectedLoss = this.getPrincipalOutstanding() - this.netInterestEarned;
    }

    private getPrincipalOutstanding() {
        return this.investedAmount - this.emiSplit.principal - this.prepaymentSplit.principal + this.capitalisedInterest;
    }

    private getWriteOffBy(writeOffTag: WriteOffTag, writtenOff: WriteOff): WriteOffHistory {
        return writtenOff.history.find(history => history.tag === writeOffTag);
    }

    private getPaidEmiPrincipal(emiSchedule: EmiSchedule, lenderShare: number): number {
        let principalPaid = 0;
        emiSchedule.filter(emi => emi.status === EmiStatus.Completed || emi.status === EmiStatus.Received)
            .map(paidEmi => {
                principalPaid += paidEmi.principal_repayment;
            });
        return round(principalPaid * lenderShare, 2);
    }

    private getPrepaymentSplit(prePaymentDetails: any, lenderShare: number): SplitMoney {
        let prepaymentPrincipal = 0, prepaymentInterest = 0;
        prePaymentDetails.details.filter(prepayment => {
            return this.isPrepaymentSuccessful(prepayment);
        }).forEach(prepayment => {
            prepaymentPrincipal += prepayment.prepayment_principal;
            prepaymentInterest += prepayment.prepayment_interest;
        });
        return {
            principal: round(prepaymentPrincipal * lenderShare, 2),
            interest: round(prepaymentInterest * lenderShare, 2)
        };
    }

    private isPrepaymentSuccessful(prepayment) {
        return (prepayment.prepayment_status === PrepaymentStatus.PrepaymentCompleted) ||
            (prepayment.prepayment_status === PrepaymentStatus.PartpaymentCompleted);
    }

    private getDelayedEmiCount(emiSchedule: any): number {
        const today = moment(new Date());
        return size(emiSchedule.filter(emi => (emi.status !== EmiStatus.Completed && emi.status !== EmiStatus.Received) && moment(emi.due_on)
            .isSameOrBefore(today)));
    }

    private async getEmiMoratorium(loanId: string): Promise<EmiMoratorium> {
        const emiMoratoriums = await this.emiMoratoriumService.getEmiMoratoriumsByLoanId(loanId);
        return emiMoratoriums && emiMoratoriums.find(emiMoratorium => emiMoratorium.deletedAt === undefined);
    }

    public getOverDueEmiCount() {
        return this.loanSanctionStatus === LoanStatusType.SubStandardAsset ? WriteOffComponent.SUB_STANDARD_ASSET_OVER_DUE_EMI_COUNT : WriteOffComponent.LOSS_ASSET_OVER_DUE_EMI_COUNT;
    }

    public getDisplayableStatus() {
        return this.loanSanctionStatus === LoanStatusType.SubStandardAsset ? 'Sub Standard Asset' : 'Loss Asset';
    }

    public isSubStandardAsset() {
        return this.loanSanctionStatus === LoanStatusType.SubStandardAsset;
    }

    private getLossAssetDate(lossAsset: WriteOffHistory) {
        if (lossAsset) {
            return lossAsset.createdAt;
        }
        const firstDelayDueDate = this.getFirstDelayDueDate();
        return moment(firstDelayDueDate).add(2, 'year').toDate();
    }

    private getFirstDelayDueDate() {
        const pendingEmis = this.emiSchedule.filter(emi => emi.status === EmiStatus.NachInitiated
            || emi.status === EmiStatus.NachFailed || emi.status === EmiStatus.PDCInitiated
            || emi.status === EmiStatus.PDCFailed);
        return lodash.head(lodash.sortBy(pendingEmis, emi => emi.due_on)).due_on;
    }


}
